/**
 * Single Firebase entry point for the SPA.
 *
 * Reads the public web config from VITE_FIREBASE_* env vars (these are NOT
 * secrets — they're the standard Firebase project identifiers, gated by
 * Firestore/Storage rules and App Check) and exports the SDK handles every
 * other module in the app imports from `@/lib/firebase`.
 *
 * When VITE_USE_EMULATORS=1 we prefer the local emulator suite (firebase.json).
 * If Auth on :9099 is not running, we fall back to the configured dev project so
 * login still works without manually toggling env vars.
 */

import { setPlannerFirebaseApp } from "@amazing-wedding/ai-planner";
import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, connectAuthEmulator, type Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";
import { getStorage, connectStorageEmulator, type FirebaseStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import { resolveAuthDomain } from "./firebase-auth-domain";
import {
  EMULATOR_HOST,
  EMULATOR_PORTS,
  initFirebaseEmulatorProbe,
  usingFirebaseEmulators,
} from "./firebase-emulator-probe";

export { usingFirebaseEmulators };

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
setPlannerFirebaseApp(app);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
/** Callables (`deleteUserAccount`) live in europe-central2. */
export const functions: Functions = getFunctions(app, "europe-central2");

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

// Emulator wiring — must happen before the first read/write, and only in
// local dev. Production bundles must always talk to the real Firebase project.
await initFirebaseEmulatorProbe();
if (usingFirebaseEmulators) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
  connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
  connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
} else if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === "1") {
  console.info(
    `[firebase] Emulator suite not detected — using project "${firebaseConfig.projectId}".`,
  );
}
